import { Class } from '../../utils';
/** 
 *  
 * @class Makes an array of objects searchable
 * @constructs Septima.Search.SearchableData
 * @param {Object} options SearchableData expects these properties:
 * @param options.data {object} Array of data or a function that returns an array of data.
 * @param options.searchProperties {string[]} Array of property names in the data array to search in. If not added, all properties will be used
 * @param options.displaynameProperty {string} The name of the property in the data array that should be used as displayname
 * @param options.descriptionProperty {string} The name of the property in the data array that should be used as description
 * @param options.useAND {boolean} Use AND and not OR when multiple terms is added by the user. Default true
 */

export default Class(
/**  @lends Septima.Search.SearchableData# */
{
  /**
  * Array of data or a function that returns an array of data. If data is a function it will be 
  * called each time fetchData is called. This way the client could change the data on the fly 
  * and not keep them static. This could be used when adding an external filter.
  */
  data: [],

  /**
  * Array of property names in the data array to search in. If not added, all properties will be used
  */
  searchProperties: [],

  /**
  * The name of the property in the data array that should be used as displayname.
  */
  displaynameProperty: null,

  /**
  * The name of the property in the data array that should be used as description.
   * @type string
   */
  descriptionProperty: null,

  /**
  * Use AND and not OR when multiple terms is added by the user. Set to false to use OR
  */
  useAND: true,

  /**
  * Singular phrase, eg.: "feature"
  */
  singular: null,

  /**
  * Plural phrase, eg.: "features"
  */
  plural: null,
  initialize: function initialize(options) {
    this.singular = options.singular;
    this.plural = options.plural;
    this.useAND = options.useAND !== false;
    this.searchProperties = options.searchProperties || [];
    this.displaynameProperty = options.displaynameProperty || null;
    this.descriptionProperty = options.descriptionProperty || null;
    this.geometryProperty = options.geometryProperty || null;
    this.idProperty = options.idProperty || null;
    this.data = options.data || [];
    if (options.getDisplayname) this.getDisplayname = options.getDisplayname;
    if (options.getDescription) this.getDescription = options.getDescription;
    if (options.getGeometry) this.getGeometry = options.getGeometry;
  },
  getData: function getData() {
    if (this.data instanceof Function) return this.data();else return this.data;
  },
  query: function query(queryString) {
    var _this = this;

    var objectsToSearch = this.getData();
    var resultset = [];
    var queryTerms = queryString.split(" ");

    if (queryTerms.length > 0) {
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = objectsToSearch[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var currentObject = _step.value;
          var hit = {
            score: 0,
            object: currentObject,
            title: this.getDisplayname(currentObject)
          };

          if (hit.title !== null) {
            if (queryString === '') {
              hit.score = 1;
            } else {
              var andcount = 0;
              var andscore = 0;
              var _iteratorNormalCompletion2 = true;
              var _didIteratorError2 = false;
              var _iteratorError2 = undefined;

              try {
                for (var _iterator2 = queryTerms[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                  var term = _step2.value;
                  var score = this.match(hit, term);

                  if (score > 0) {
                    andcount++;
                    andscore += score;
                  }
                }
              } catch (err) {
                _didIteratorError2 = true;
                _iteratorError2 = err;
              } finally {
                try {
                  if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
                    _iterator2.return();
                  }
                } finally {
                  if (_didIteratorError2) {
                    throw _iteratorError2;
                  }
                }
              }

              if (this.useAND) {
                if (andcount == queryTerms.length) hit.score = andscore;
              } else {
                hit.score = andscore;
              }
            }

            if (hit.score > 0) {
              if (this.idProperty) hit.id = this.getId(currentObject);
              hit.description = this.getDescription(currentObject);
              hit.geometry = this.getGeometry(currentObject);
              resultset.push(hit);
            }
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      if (resultset.length > 0) resultset.sort(function (hit1, hit2) {
        return _this.compareHits(hit1, hit2);
      });
    }

    return resultset;
  },
  isGettable: function isGettable() {
    return this.idProperty !== null;
  },
  get: function get(id) {
    var objectsToSearch = this.getData();
    var _iteratorNormalCompletion3 = true;
    var _didIteratorError3 = false;
    var _iteratorError3 = undefined;

    try {
      for (var _iterator3 = objectsToSearch[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
        var currentObject = _step3.value;
        if (this.getId(currentObject) == id) return {
          score: 0,
          object: currentObject,
          title: this.getDisplayname(currentObject),
          description: this.getDescription(currentObject),
          id: id
        };
      }
    } catch (err) {
      _didIteratorError3 = true;
      _iteratorError3 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion3 && _iterator3.return != null) {
          _iterator3.return();
        }
      } finally {
        if (_didIteratorError3) {
          throw _iteratorError3;
        }
      }
    }

    return null;
  },
  compareHits: function compareHits(hit1, hit2) {
    if (hit2.score === hit1.score) {
      if (isNaN(hit1.title) || isNaN(hit2.title)) {
        return hit1.title.toString().localeCompare(hit2.title.toString());
      } else {
        return hit1.title - hit2.title;
      }
    } else return hit2.score - hit1.score;
  },
  getId: function getId(object) {
    var id = null;
    if (this.idProperty) id = object[this.idProperty];
    return id;
  },

  /**
  * Used for getting the displayname
  * @return {String}
  */
  getDisplayname: function getDisplayname(object) {
    var displayName = null;
    if (this.displaynameProperty) displayName = object[this.displaynameProperty];
    return displayName;
  },

  /**
  * Used for getting the description
  * @return {String}
  */
  getDescription: function getDescription(object) {
    var description = '';
    if (this.descriptionProperty) description = '' + object[this.descriptionProperty];
    return description;
  },
  getGeometry: function getGeometry(object) {
    var geometry = null;
    if (this.geometryProperty) geometry = object[this.geometryProperty];
    return geometry;
  },

  /**
  * Method that defines what to search in 
  * @return {Integer}
  * */
  match: function match(potentialHit, str) {
    var score = 0;

    if (str === "") {
      score = 1;
    } else {
      //Factor two for scores in title
      score = 2 * this.getScore(potentialHit.title, str);
      var valueToSearch;

      if (this.searchProperties.length) {
        var _iteratorNormalCompletion4 = true;
        var _didIteratorError4 = false;
        var _iteratorError4 = undefined;

        try {
          for (var _iterator4 = this.searchProperties[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
            var searchProperty = _step4.value;
            valueToSearch = potentialHit.object[searchProperty];
            if (valueToSearch && valueToSearch !== null && valueToSearch !== '') score += this.getScore(valueToSearch, str);
          }
        } catch (err) {
          _didIteratorError4 = true;
          _iteratorError4 = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion4 && _iterator4.return != null) {
              _iterator4.return();
            }
          } finally {
            if (_didIteratorError4) {
              throw _iteratorError4;
            }
          }
        }
      } else for (var name in potentialHit.object) {
        valueToSearch = potentialHit.object[name];
        if (valueToSearch !== null && valueToSearch !== '') score += this.getScore(valueToSearch, str);
      }
    }

    return score;
  },

  /**
  * Get the score for a single string 
  * @return {Integer}
  * */
  getScore: function getScore(stringIn, searchstr) {
    var val = stringIn.toString();
    if (val.toLowerCase().indexOf(searchstr.toLowerCase()) === 0) return 2;else if (val.toLowerCase().indexOf(' ' + searchstr.toLowerCase()) > 0) return 1;else return 0;
  },
  CLASS_NAME: 'Septima.Search.SearchableData'
});